import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2, Pencil } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import Swal from "sweetalert2";
import { apiErrorMessage } from "Common/constants/apiErrors";

interface Product {
  id: number;
  name: string;
}

interface ProductPrice {
  id: number;
  product_id: number;
  product_name: string;
  price: string | number;
  created_at: string;
  created_at_jalali: string;
  updated_at: string;
  updated_at_jalali: string;
}

const formatPrice = (value: string | number) => {
  if (!value) return "";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US");
};

const parsePrice = (value: string) => {
  return parseFloat(value.replace(/,/g, "")) || 0;
};

const Index = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ProductPrice[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [show, setShow] = useState<boolean>(false);
  const [eventData, setEventData] = useState<ProductPrice | null>(null);
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [productsLoading, setProductsLoading] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  }, []);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // دریافت لیست محصولات
  useEffect(() => {
    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get("https://portal-api.mahanpanel.com/products/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProducts(res.data);
      } catch (error: any) {
        console.error("Error fetching products:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, [navigate]);

  // دریافت لیست قیمت‌ها
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get("https://portal-api.mahanpanel.com/product-prices/", {
          headers: { Authorization: `Bearer ${token}` },
          params: sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder },
        });
        setData(Array.isArray(res.data) ? res.data : res.data?.items || []);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching product prices:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        setErrorMessage("مشکلی در دریافت لیست قیمت‌ها رخ داده است.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reload, navigate, sortBy, sortOrder]);

  // حذف قیمت
  const onClickDelete = (row: ProductPrice) => {
    setDeleteModal(true);
    setEventData(row);
  };

  const handleDelete = async () => {
    if (!eventData) return;
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.delete(
        `https://portal-api.mahanpanel.com/product-prices/${eventData.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "قیمت محصول با موفقیت حذف شد", "success");
        setData((prev) => prev.filter((item) => item.id !== eventData.id));
        setDeleteModal(false);
        setEventData(null);
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("عملیات ناموفق", "خطا در حذف قیمت محصول", "error");
      setDeleteModal(false);
      setEventData(null);
    }
  };

  // فرم ثبت/ویرایش قیمت
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      product_id: "",
      price: "",
    },
    validationSchema: Yup.object({
      product_id: Yup.string().required("لطفاً محصول را انتخاب کنید"),
      price: Yup.string()
        .required("لطفاً قیمت را وارد کنید")
        .test("is-valid-number", "قیمت باید عدد معتبر باشد", (value) => {
          if (!value) return false;
          const num = parsePrice(value);
          return !isNaN(num) && num >= 0;
        }),
    }),
    onSubmit: async (values) => {
      const payload = {
        product_id: Number(values.product_id),
        price: parsePrice(values.price),
      };

      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        let res;

        if (editMode && editingId) {
          // ویرایش قیمت
          res = await axios.patch(
            `https://portal-api.mahanpanel.com/product-prices/${editingId}`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (res.status === 200) {
            const productName = products.find(p => p.id === Number(values.product_id))?.name || "";
            Swal.fire("عملیات موفق", `قیمت محصول "${productName}" بروزرسانی شد`, "success");
            setReload(!reload);
            toggle();
          }
        } else {
          // ثبت قیمت جدید
          res = await axios.post(
            "https://portal-api.mahanpanel.com/product-prices/",
            payload,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (res.status === 201) {
            const productName = products.find(p => p.id === Number(values.product_id))?.name || "";
            Swal.fire("عملیات موفق", `قیمت محصول "${productName}" ثبت شد`, "success");
            setReload(!reload);
            toggle();
          }
        }
      } catch (error: any) {
        console.error("Submit error:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        if (error.response?.status === 422) {
          // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
          // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
          let msg = apiErrorMessage(error, "اطلاعات وارد شده نامعتبر است");
          Swal.fire("خطا", msg, "error");
        } else {
          Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت یا ویرایش قیمت"), "error");
        }
      }
    },
  });

  // باز کردن مودال برای ویرایش
  const handleEdit = (row: ProductPrice) => {
    setEditMode(true);
    setEditingId(row.id);
    validation.setValues({
      product_id: String(row.product_id),
      price: formatPrice(row.price),
    });
    setShow(true);
  };

  // باز/بستن مودال
  const toggle = useCallback(() => {
    setShow((prev) => !prev);
    if (show) {
      setEditMode(false);
      setEditingId(null);
      validation.resetForm();
      setEventData(null);
    } else {
      setEditMode(false);
      setEditingId(null);
      validation.resetForm();
      setEventData(null);
    }
  }, [show, validation]);

  const handleSubmitButton = (e: React.MouseEvent) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, "");
    if (rawValue === "" || /^\d*\.?\d*$/.test(rawValue)) {
      validation.setFieldValue("price", rawValue);
    }
  };

  const handlePriceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        validation.setFieldValue("price", formatPrice(String(num)));
      }
    }
  };

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "id",
        cell: (cell: any) => <span>{cell.getValue()}</span>,
      },
      {
        header: "نام محصول",
        accessorKey: "product_name",
        meta: { serverSortBy: "product_name" },
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link
            to="#!"
            className="transition-all duration-150 ease-linear text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "قیمت (تومان)",
        accessorKey: "price",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? formatPrice(value) : "-";
        },
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => (
          <div className="flex gap-3">
            <Link to="#!" onClick={() => handleEdit(cell.row.original)}>
              <Pencil className="size-4 text-blue-500 hover:text-blue-700" />
            </Link>
            <Link to="#!" onClick={() => onClickDelete(cell.row.original)}>
              <Trash2 className="size-4 text-red-500 hover:text-red-700" />
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="ثبت قیمت محصول" title="داده‌های اولیه" />
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      {errorMessage && (
        <div className="text-center text-red-500 mb-3">{errorMessage}</div>
      )}
      <div className="card" id="employeeTable rtl">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع قیمت‌ها (<b className="total-Employs">{data.length}</b>)
            </h6>
            <div className="shrink-0">
              <Link
                to="#!"
                onClick={() => {
                  setEditMode(false);
                  setEditingId(null);
                  validation.resetForm();
                  setShow(true);
                }}
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
              >
                <span className="align-middle rtl">ثبت قیمت جدید</span>{" "}
                <Plus className="inline-block size-4 rtl" />
              </Link>
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={10}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text="هیچ قیمتی ثبت نشده است" />
          )}
        </div>
      </div>

      {/* مودال ثبت/ویرایش قیمت */}
      <Modal
        show={show}
        onHide={toggle}
        modal-center="true"
        dialogClassName="w-screen md:w-[30rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {editMode ? "ویرایش قیمت محصول" : "اضافه کردن قیمت محصول"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form
            className="create-form"
            id="create-form"
            onSubmit={validation.handleSubmit}
          >
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              {/* انتخاب محصول */}
              <div className="xl:col-span-11 dark:text-white text-gray-800">
                <div className="flex items-center">
                  <label
                    htmlFor="product_id"
                    className="text-base font-medium mr-2 w-1/4"
                  >
                    محصول:
                  </label>
                  <select
                    id="product_id"
                    className="form-select font-[12px] flex-1"
                    name="product_id"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.product_id}
                    disabled={productsLoading}
                  >
                    <option value="">انتخاب محصول</option>
                    {products.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
                {validation.touched.product_id && validation.errors.product_id && (
                  <p className="text-red-400 mr-24 mt-2">
                    {typeof validation.errors.product_id === "string"
                      ? validation.errors.product_id
                      : ""}
                  </p>
                )}
                {productsLoading && (
                  <p className="text-gray-400 mr-24 mt-1 text-xs">در حال بارگذاری محصولات...</p>
                )}
              </div>

              {/* قیمت */}
              <div className="xl:col-span-11 dark:text-white text-gray-800">
                <div className="flex items-center">
                  <label
                    htmlFor="price"
                    className="text-base font-medium mr-2 w-1/4"
                  >
                    قیمت (تومان):
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="price"
                    className="form-input font-[12px] flex-1"
                    placeholder="مثلاً ۱٬۲۰۰٬۰۰۰"
                    name="price"
                    autoComplete="off"
                    onChange={handlePriceChange}
                    onBlur={handlePriceBlur}
                    value={validation.values.price}
                  />
                </div>
                {validation.touched.price && validation.errors.price && (
                  <p className="text-red-400 mr-24 mt-2">
                    {typeof validation.errors.price === "string"
                      ? validation.errors.price
                      : ""}
                  </p>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleSubmitButton}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:ring focus:ring-custom-100 dark:focus:ring-custom-400/20"
          >
            {editMode ? "ویرایش" : "ثبت"}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
